import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of, switchMap } from 'rxjs';
import { ArkUserType, ClientCompanyViewModel, ClientModel, ListItem } from '../../core/api/models';
import { ClientsApiService } from '../../core/api/services';
import { Utils } from '../../core/utils';
import { AuthService } from '../../main/auth.service';
import { ClientService } from '../';
import { AlertService } from '../../shared/services/alert.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-client-companies-component',
  templateUrl: './client-companies.component.html',
  standalone: false
})
export class ClientCompaniesComponent implements OnInit {
  
  currentClient?: ClientModel;
  companies: ClientCompanyViewModel[] = [];
  currentItem?: ClientCompanyViewModel;
  loading = true;
  saving = false;


  readonly fields = [
    { name: 'BusinessName', label: 'Business Name', type: 'text', max: 255, required: true },
    { name: 'BusinessAbbreviation', label: 'Business Abbreviation', type: 'text', max: 50, required: true },
    { name: 'BusinessFullName', label: 'Business Full Name', type: 'text', max: 255, required: true },
    { name: 'BusinessAddress', label: 'Business Address', type: 'text', max: 255, required: true },
    { name: 'BusinessPhone', label: 'Business Phone', type: 'tel', max: 50, required: true },
    { name: 'BusinessFax', label: 'Business Fax', type: 'tel', max: 50 },
    { name: 'BusinessWebsite', label: 'Business Website', type: 'text', max: 512 },
    { name: 'BusinessSupportUrl', label: 'Support URL', type: 'text', max: 512 },
    { name: 'TaxID', label: 'Tax ID / EIN', type: 'text', max: 50 },
    { name: 'CompanyNPI', label: 'Company NPI', type: 'text', max: 25 },
    { name: 'CompanyPTAN', label: 'Company PTAN', type: 'text', max: 50 },
    { name: 'CompanyMedicaidNumber', label: 'Company Medicaid Number', type: 'text', max: 50 },
    { name: 'ProfessionalLiabilityNumber', label: 'Professional Liability Number', type: 'text', max: 25 },
    { name: 'ProfessionalLiabilityOccurrenceAmount', label: 'Liability Occurrence Amount', type: 'number' },
    { name: 'ProfessionalLiabilityAggregateAmount', label: 'Liability Aggregate Amount', type: 'number' },
    { name: 'FromEmailAddress', label: 'From Email Address', type: 'email', max: 255 },
    { name: 'EmailDisplayName', label: 'Email Display Name', type: 'text', max: 255 }
  ];
  itemFormGroup: FormGroup;

  constructor(
    private readonly authService: AuthService,
    private readonly clientService: ClientService,
    private readonly route: ActivatedRoute,
    private readonly alertService: AlertService,
    private readonly apiService: ClientsApiService,
    formBuilder: FormBuilder
  ) {
    const controls: Record<string, unknown> = { IsMain: [null] };
    for (const field of this.fields) {
      const validators = [];
      if (field.required) validators.push(Validators.required);
      if (field.max) validators.push(Validators.maxLength(field.max));
      if (field.type === 'email') validators.push(Validators.email);
      controls[field.name] = [null, validators];
    }
    this.itemFormGroup = formBuilder.group(controls);
  }

  ngOnInit(): void {
    this.authService.checkIfAutorized([ArkUserType.Admin]);



     this.clientService.getCurrentClient().subscribe((client) => {

      if (client){
         this.currentClient = client;


         this.apiService.GetClientCompanies({ clientID: this.currentClient.ID||0 }).pipe(catchError(error => {
          this.alertService.error(Utils.getAjaxError(error));
          return of(undefined);
        })).subscribe((companies) => {

            if (companies) {
              this.companies = companies;
             this.selectCompany(this.companies.find(company => company.IsMain)?.ID ?? this.companies[0]?.ID);

              }
          this.loading = false;

         });
      }
    });



  }

  selectCompany(id?: number): void {
    if (this.saving) return;
    this.currentItem = this.companies.find(company => company.ID === id);
    if (this.currentItem) this.itemFormGroup.reset(this.currentItem);
  }

  save(): void {
    if (!this.currentItem || this.saving) return;
    this.itemFormGroup.markAllAsTouched();
    if (this.itemFormGroup.invalid) {
      this.alertService.error(Utils.getFormErrors(this.itemFormGroup).join('\n'));
      return;
    }
    const submitted: ClientCompanyViewModel = { ...this.currentItem, ...this.itemFormGroup.getRawValue() };
    const clientID = this.currentClient?.ID||0;
    this.saving = true;
    this.apiService.SaveClientCompany({ clientID, body: submitted })
      .subscribe({
        next: saved => {
          this.saving = false;
          this.companies = this.companies.map(company => company.ID === saved.ID ? saved : company);
          if (this.currentItem?.ID === submitted.ID) {
            this.currentItem = saved;
            this.itemFormGroup.reset(saved);
          }
          this.alertService.success('Company saved successfully.');
        },
        error: error => {
          this.saving = false;
          this.alertService.error(Utils.getAjaxError(error));
        }
      });
  }


}
